import { getFullContent } from '../HighlightableString';
import { enumerateEachHighlightableString } from '../util/enumerateEachHighlightableString';
import { findRegex, findSubstrings, FoundTextOccurance } from '../util/findText';
import { mergeTerms } from '../util/mergeTerms';
import { Highlighter, HighlighterId, HighlighterResult } from './Highlighter';

export interface TextHighlighterArgs {
  term: string;
  regex: boolean;
  caseSensitive: boolean;
  returnFullHighlightedString: boolean;
}

export abstract class AbstractTextHighlighter implements Highlighter {
  abstract get id(): HighlighterId;

  args: TextHighlighterArgs;

  constructor(args: TextHighlighterArgs) {
    this.args = args;
  }

  process(data: any): HighlighterResult {
    const result: HighlighterResult = {
      foundTerms: [],
      changes: {},
    };

    let findOccurancesClosure: (s: string) => FoundTextOccurance[];

    const isEmptyTerm = this.args.term.length === 0;

    if (isEmptyTerm) {
      findOccurancesClosure = (_s) => [];
    } else if (this.args.regex) {
      const regex = new RegExp(this.args.term, this.args.caseSensitive ? 'g' : 'gi');
      findOccurancesClosure = (s) => findRegex(s, regex);
    } else if (this.args.caseSensitive) {
      findOccurancesClosure = (s) => findSubstrings(s, this.args.term);
    } else {
      const termToUse = this.args.term.toLowerCase();
      findOccurancesClosure = (s) => findSubstrings(s.toLowerCase(), termToUse);
    }

    result.changes = enumerateEachHighlightableString({
      input: data,
      returnFullHighlightedString: this.args.returnFullHighlightedString,
      callback: (s) => {
        if (isEmptyTerm) {
          return {
            occurancesFound: false,
            shouldCollapseIfNoOccurancesFound: undefined,
          };
        }

        const foundOccurances = findOccurancesClosure(getFullContent(s));
        if (foundOccurances.length === 0) {
          return {
            occurancesFound: false,
            shouldCollapseIfNoOccurancesFound: undefined,
          };
        }

        const newTerms = mergeTerms(s, this.id, foundOccurances);
        result.foundTerms.push(...newTerms);
        return {
          occurancesFound: true,
          shouldCollapseIfNoOccurancesFound: undefined,
        };
      },
    });

    return result;
  }
}
